// MULTI-SPACE-ACCESS (founder request 2026-10-30): a channel or doc lives in one home space and can
// also be shared into more spaces, each with a level its members get. `SpaceSharesField` is the
// one control for it — in New channel and New doc ("Also share with", after the home space) and
// in the item's Share dialog (`ShareSpacesDialog`) to change it later. Levels are the space roles
// the access model already has: Full access (owner), Can edit (editor), Can view (viewer).
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  InfoTip,
  NativeSelect,
  NativeSelectOption,
  toast,
} from "@companyos/ui";
import { PlusIcon, Share2Icon, XIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useId, useState } from "react";

/** KB-UPLOAD-2: + `reader` — a doc's true read-only level (no comments, no edits). */
export type ShareLevel = "owner" | "editor" | "viewer" | "reader";
export type ShareInput = { space_id: string; level: ShareLevel };
export type ShareRow = ShareInput & { object_id: string; object_type: "channel" | "page" };
type SpaceOption = { id: string; name: string; kind: string; archived_at: string | null };

export const LEVEL_LABEL: Record<ShareLevel, string> = {
  owner: "Full access",
  editor: "Can edit",
  viewer: "Can view",
  reader: "Read only",
};
const LEVELS: readonly ShareLevel[] = ["viewer", "editor", "owner"];
/** A doc's levels (05 `page`): Read only, Can comment (its thread), Can edit. */
export const DOC_LEVELS: readonly ShareLevel[] = ["reader", "viewer", "editor"];

/** A level's name: a doc's viewers comment in its thread, a channel's only read. */
export const levelLabel = (l: ShareLevel, what: string): string =>
  what === "doc" && l === "viewer"
    ? "Can comment"
    : what === "doc" && l === "reader"
      ? "Can view"
      : LEVEL_LABEL[l];

/** KB-UPLOAD-2: a doc level's (i) line. SHARE-EVERYONE: Can view (was Read only), Full access. */
export const DOC_LEVEL_HELP =
  "Can view: reads it, no comments or edits. Can edit: changes it. Full access: edits, shares and manages it. The most permissive entry wins. Agents get the same rights.";

/** The spaces an item may be shared into: live, not personal, not its home. */
export function shareTargets(spaces: readonly SpaceOption[], homeId: string): SpaceOption[] {
  return spaces.filter((s) => !s.archived_at && s.kind !== "personal" && s.id !== homeId);
}

/** "Also share with": one row per extra space (space, level, remove) and an Add space button. */
export function SpaceSharesField({
  spaces,
  homeSpaceId,
  value,
  onChange,
  what,
  disabled = false,
  levels = LEVELS,
  defaultLevel = "viewer",
  hideLabel = false,
}: {
  spaces: readonly SpaceOption[];
  homeSpaceId: string;
  value: readonly ShareInput[];
  onChange: (next: ShareInput[]) => void;
  /** "channel" or "doc", for the labels. */
  what: string;
  disabled?: boolean;
  /** The levels offered (a doc: Read only, Can comment, Can edit; Full access is a channel's). */
  levels?: readonly ShareLevel[];
  /** KB-UPLOAD-2: the level a newly added space starts at (the upload's "Add as" default). */
  defaultLevel?: ShareLevel;
  /** KB-UPLOAD-2: the upload's "Share with" step names the group itself. */
  hideLabel?: boolean;
}) {
  const id = useId();
  const targets = shareTargets(spaces, homeSpaceId);
  const rows = value.filter((v) => v.space_id !== homeSpaceId);
  const unused = targets.filter((t) => !rows.some((r) => r.space_id === t.id));
  const set = (i: number, patch: Partial<ShareInput>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const nameOf = (sid: string) => spaces.find((s) => s.id === sid)?.name ?? "A space";
  return (
    <Field data-slot="space-shares">
      <div className={hideLabel ? "sr-only" : "flex items-center gap-1"}>
        <FieldLabel id={`${id}-label`}>{hideLabel ? "Spaces" : "Also share with"}</FieldLabel>
        {hideLabel ? null : (
          <InfoTip label="About Also share with">
            The {what} stays in its home space. Members of each space added here get the level you
            pick{what === "doc" ? "; moving, archiving and sharing stay with the home space" : ""}.
          </InfoTip>
        )}
      </div>
      {/* biome-ignore lint/a11y/useSemanticElements: a list of share rows, labelled as a group. */}
      <ul
        role="group"
        aria-labelledby={`${id}-label`}
        className="m-0 flex list-none flex-col gap-1.5 p-0"
      >
        {rows.map((r, i) => (
          <li key={r.space_id} className="flex items-center gap-1.5" data-slot="space-share-row">
            <NativeSelect
              aria-label={`Shared space ${i + 1}`}
              className="min-w-0 flex-1"
              value={r.space_id}
              disabled={disabled}
              onChange={(e) => set(i, { space_id: e.target.value })}
            >
              {[...targets.filter((t) => t.id === r.space_id), ...unused].map((t) => (
                <NativeSelectOption key={t.id} value={t.id}>
                  {t.name}
                </NativeSelectOption>
              ))}
              {targets.some((t) => t.id === r.space_id) ? null : (
                <NativeSelectOption value={r.space_id}>{nameOf(r.space_id)}</NativeSelectOption>
              )}
            </NativeSelect>
            <NativeSelect
              aria-label={`Access in ${nameOf(r.space_id)}`}
              className="w-36"
              value={r.level}
              disabled={disabled}
              onChange={(e) => set(i, { level: e.target.value as ShareLevel })}
            >
              {levels.map((l) => (
                <NativeSelectOption key={l} value={l}>
                  {levelLabel(l, what)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Stop sharing with ${nameOf(r.space_id)}`}
              title="Remove"
              disabled={disabled}
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
            >
              <XIcon aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>
      {unused.length ? (
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() =>
              onChange([...rows, { space_id: unused[0]?.id ?? "", level: defaultLevel }])
            }
          >
            <PlusIcon aria-hidden="true" />
            Add space
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="m-0 text-xs text-muted-foreground">No other space to share with.</p>
      ) : null}
    </Field>
  );
}

/**
 * The item's Share dialog: its home space (fixed; Move changes it) and the same "Also share
 * with" control. Saves the whole set (`channels.shares.set` / `pages.shares.set`).
 */
export function ShareSpacesDialog({
  open,
  onOpenChange,
  title,
  what,
  spaces,
  homeSpaceId,
  current,
  ready,
  levels,
  save,
  onSaved,
  children,
}: {
  /** KB-UPLOAD-2: more of the item's sharing below the spaces (a doc's people and DEs). */
  children?: ReactNode | ((pending: boolean) => ReactNode);
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  what: string;
  spaces: readonly SpaceOption[];
  homeSpaceId: string;
  current: readonly ShareInput[];
  /** The saved set has loaded (Codex r1: never save over one that has not: it would drop it). */
  ready: boolean;
  levels?: readonly ShareLevel[];
  save: (shares: ShareInput[]) => Promise<unknown>;
  onSaved?: () => void | Promise<void>;
}) {
  const [value, setValue] = useState<ShareInput[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reset when it opens or the saved set loads/changes (by value: callers pass a fresh array).
  const saved = JSON.stringify(current.map((c) => ({ space_id: c.space_id, level: c.level })));
  useEffect(() => {
    if (open) {
      setValue(JSON.parse(saved) as ShareInput[]);
      setError(null);
    }
  }, [open, saved]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await save(value);
      toast.add({ title: `Sharing of ${title} saved`, type: "success" });
      await onSaved?.();
      onOpenChange(false);
    } catch {
      setError("The sharing could not be saved.");
    } finally {
      setPending(false);
    }
  };
  const home = spaces.find((s) => s.id === homeSpaceId)?.name ?? "Its space";
  return (
    <Dialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Share {title}</DialogTitle>
            <DialogDescription>Lives in {home}.</DialogDescription>
          </DialogHeader>
          <SpaceSharesField
            spaces={spaces}
            homeSpaceId={homeSpaceId}
            value={value}
            onChange={setValue}
            what={what}
            disabled={pending || !ready}
            {...(levels ? { levels } : {})}
          />
          {typeof children === "function" ? children(pending || !ready) : children}
          {error ? <FieldError>{error}</FieldError> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending || !ready}>
              <Share2Icon aria-hidden="true" />
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A small "shared" marker for a row shown in a space it is shared into (not its home). */
export function SharedMarker({ home }: { home: string | null }) {
  return (
    <span
      data-slot="shared-marker"
      title={home ? `Shared from ${home}` : "Shared from another space"}
      className="inline-flex shrink-0 items-center text-faint"
    >
      <Share2Icon aria-label={home ? `Shared from ${home}` : "Shared"} className="size-3" />
    </span>
  );
}
